import React, { useState, useEffect } from "react";
import "./MobileAppsPage.css";
import BASE_URL from "../apiConfig";
import useRecaptcha from "../hooks/useRecaptcha";

/* HERO IMAGE */
import bgImage from "../images/mobile.jpg";

/* APP IMAGES */
import collectionImg from "../images/app1.png";
import originationImg from "../images/app2.png";
import recoveryImg from "../images/app3.png";
import chitImg from "../images/app4.png";
import rdImg from "../images/app5.png";
import customerImg from "../images/app6.png";
import managerImg from "../images/app7.png";

function MobileAppsPage() {

    const [widgetId, setWidgetId] = useState(null);
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const { getResponse } = useRecaptcha();

    useEffect(() => {
        if (window.grecaptcha) {
            const container = document.getElementById("recaptcha-mobile-apps");
            if (container && container.innerHTML === "") {
                try {
                    const id = window.grecaptcha.render(container, {
                        // sitekey: "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI",
                         sitekey: window.RECAPTCHA_SITE_KEY,
                    });
                    setWidgetId(id);
                } catch (err) {
                    console.error("reCAPTCHA MobileAppsPage render error:", err);
                }
            }
        }
    }, []);

    const [formData, setFormData] = useState({
        full_name: "",
        phone: "",
        email: "",
        organization: "",
        challenges: ""
    });

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.full_name || !formData.phone) {
            setMessage("❌ Name & Phone required");
            return;
        }

        setLoading(true);

        try {
            const token = getResponse(widgetId);
            if (!token) {
                setMessage("❌ Please complete the reCAPTCHA");
                setLoading(false);
                return;
            }
            const fd = new FormData();
            Object.keys(formData).forEach(k => fd.append(k, formData[k]));
            fd.append("_type", "mobileapps_demo");
            fd.append("tk", token);

            const res = await fetch(
                BASE_URL + "/DemoApi/saveData",
                { method: "POST", body: fd }
            );

            const result = await res.json();

            if (result.status) {
                setMessage("✅ Demo Request Submitted");
                setFormData({
                    full_name: "",
                    phone: "",
                    email: "",
                    organization: "",
                    challenges: ""
                });
            } else {
                setMessage("❌ " + (result.message || "Verification Failed"));
            }

        } catch (err) {
            console.error("Submission Error:", err);
            setMessage("❌ Submission Failed. Check Console.");
        }

        setLoading(false);
    };


    /* ================= DATA ================= */

    const apps = [

        {
            title: "Collection App",
            subtitle: "For Field Agents & Recovery Staff",
            image: collectionImg,
            features: [
                "Offline Collection Mode – Work without internet, sync when connected",
                "GPS Location Tracking – Verify agent presence at collection points",
                "Digital Receipt Generation – Instant SMS/WhatsApp receipts to customers",
                "Multiple Payment Modes – Cash, UPI, card, and cheque collection",
                "Daily Collection Summary – Auto-reconciliation with cash deposit tracking",
                "Route Optimization – Smart visit planning for maximum efficiency",
                "Customer Feedback Capture – Service quality and grievance recording"
            ]
        },

        {
            title: "Loan Origination App",
            subtitle: "For Sales Team & Field Officers",
            image: originationImg,
            features: [
                "Mobile Application Capture – Complete loan application with photos",
                "eKYC Integration – Aadhaar, PAN, and document verification",
                "Ornament Image Upload – Gold loan specific with multiple angle photos",
                "Live Gold Rate Check – Instant loan amount calculation",
                "Credit Scoring – Preliminary eligibility assessment on-the-spot",
                "Digital Signatures – Customer e-signature for immediate approval",
                "Lead Management – Follow-up reminders and conversion tracking"
            ]
        },

        {
            title: "Loan Collection App",
            subtitle: "Specialized for Loan Recovery Teams",
            image: recoveryImg,
            features: [
                "NPA Bucket-wise Lists – Day-wise overdue accounts for follow-up",
                "Promise-to-Pay Recording – Capture customer commitments with dates",
                "Legal Case Tracking – Update status of court/arbitration proceedings",
                "Field Visit Proof – Timestamped photos and location verification",
                "Escalation Management – Raise issues to supervisors instantly",
                "Performance Dashboard – Individual and team collection metrics"
            ]
        },

        {
            title: "Chit Collection App",
            subtitle: "For Chit Fund Field Collection",
            image: chitImg,
            features: [
                "Group-wise Collection Sheets – Auto-generated based on installment due",
                "Chit Auction Participation – Bid submission and result viewing",
                "Prize Disbursement Tracking – Record prize money handover",
                "Member Communication – Installment reminders and auction alerts",
                "Foreman Commission Calculation – Real-time commission tracking"
            ]
        },

        {
            title: "RD Collection App",
            subtitle: "Recurring Deposit Installment Collection",
            image: rdImg,
            features: [
                "Customer-wise Due List – Daily collection schedule",
                "Installment Receipts – Digital receipts with next due date",
                "Missed Payment Tracking – Follow-up list generation",
                "Auto-Renewal Confirmation – Customer consent for renewal",
                "Premature Closure Requests – Capture and forward to admin"
            ]
        },

        {
            title: "Customer Mobile App",
            subtitle: "Self-Service Portal for Customers",
            image: customerImg,
            features: [
                "Account Dashboard – View all loans, deposits, and investments",
                "Digital Statement Access – Download monthly statements",
                "Online Payments – UPI, net banking, and wallet payments",
                "Online GoldLoan – gold loan request by online confirmation",
                "Service Requests – Loan renewal, foreclosure, document requests",
                "Rate Alerts – Gold rate notifications",
                "Document Vault – Secure storage of agreements"
            ]
        },

        {
            title: "Manager / Owner Mobile App",
            subtitle: "Executive Dashboard",
            image: managerImg,
            features: [
                "Real-time Business Snapshot – Key metrics on the go",
                "Approval Workflows – Approve loans & disbursements",
                "Alert Management – Critical alerts",
                "Branch Performance Comparison",
                "Cash Position – Daily liquidity status",
                "Biometric Approvals – Secure authorization"
            ]
        }

    ];

    return (
        <>

            {/* HERO */}

            <div
                className="mobileapps-hero"
                style={{ backgroundImage: `url(${bgImage})` }}
            >

                <div className="mobileapps-wrapper">

                    <div className="mobileapps-left">

                        <h1>Mobile Applications & Online Services</h1>

                        <h3>
                            Complete Digital Ecosystem for Modern Financial Operations
                        </h3>

                        <p>
                            Unified mobile platform enabling field operations,
                            collections, onboarding, approvals and customer
                            self-service across financial institutions.
                        </p>

                    </div>

                    <div className="mobileapps-right">

                        <form className="demo-form-mobile" onSubmit={handleSubmit}>

                            <input
                                name="full_name"
                                placeholder="Full Name*"
                                value={formData.full_name}
                                onChange={handleChange}
                            />

                            <input
                                name="phone"
                                placeholder="Phone Number*"
                                value={formData.phone}
                                onChange={handleChange}
                            />

                            <input
                                name="email"
                                placeholder="Email Address"
                                value={formData.email}
                                onChange={handleChange}
                            />

                            <input
                                name="organization"
                                placeholder="Organization"
                                value={formData.organization}
                                onChange={handleChange}
                            />

                            <textarea
                                name="challenges"
                                placeholder="What challenges are you looking to solve?"
                                value={formData.challenges}
                                onChange={handleChange}
                            />

                            <div
                                id="recaptcha-mobile-apps"
                                style={{ marginBottom: "15px" }}
                            ></div>

                            <button disabled={loading}>
                                {loading ? "Submitting..." : "Book Demo"}
                            </button>

                            {message && <p>{message}</p>}

                        </form>

                    </div>
                </div>
            </div>


            {/* APP SECTIONS */}

            <div className="mobileapps-enterprise">

                {apps.map((app, i) => (
                    <div
                        className={`mobile-row ${i % 2 !== 0 ? "reverse" : ""}`}
                        key={i}
                    >

                        <div className="mobile-image">
                            <img src={app.image} alt="" />
                        </div>

                        <div className="mobile-content">

                            <h2>{app.title}</h2>
                            <h4>{app.subtitle}</h4>

                            <ul>
                                {app.features.map((f, idx) => (
                                    <li key={idx}>{f}</li>
                                ))}
                            </ul>

                        </div>

                    </div>
                ))}

            </div>

        </>
    );
}

export default MobileAppsPage;